import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../contexts/TenantContext';
import { 
  getCurrentTimezoneConfig, 
  getTimezoneInfo, 
  setTimezoneConfig,
  getAvailableTimezones
} from '../../utils/timezoneUtils';
import {
  setAutoFillEnabled,
  setAutoFillFromDatabase,
  setAutoFillFromDrafts,
  getAutoFillSettings
} from '../../utils/autoFillUtils';

export default function AccountSettings() {
  const { user } = useAuth0();
  const { currentOrganization, currentSchool } = useTenant();
  
  // Settings state
  const [currentTimezone, setCurrentTimezone] = useState('LOCAL');
  const [timezoneInfo, setTimezoneInfo] = useState(null);
  const [autoFillSettings, setAutoFillSettingsState] = useState({
    enabled: true,
    draftsEnabled: true,
    databaseEnabled: true
  });
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  
  // Load current timezone and auto-fill configurations
  useEffect(() => {
    const loadSettings = () => {
      // Load timezone settings
      const timezoneConfig = getCurrentTimezoneConfig();
      const timezoneData = getTimezoneInfo();
      
      setCurrentTimezone(timezoneConfig);
      setTimezoneInfo(timezoneData);
      
      // Load auto-fill settings
      const autoFillData = getAutoFillSettings();
      
      setAutoFillSettingsState(autoFillData);
    };
    
    loadSettings();
  }, []);
  
  // Available timezone options
  const timezoneOptions = getAvailableTimezones();
  
  // Helper functions for new timezone UI
  const getUSTimezones = () => {
    const now = new Date();
    
    const usTimezones = [
      {
        value: 'LOCAL',
        region: 'Local Device Time',
        designation: 'Use your device\'s current timezone',
        preview: now.toLocaleString('en-US', { 
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
        })
      },
      {
        value: 'America/New_York',
        region: 'Eastern Time',
        designation: 'America/New_York (EST/EDT)',
        preview: now.toLocaleString('en-US', { 
          timeZone: 'America/New_York',
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
        })
      },
      {
        value: 'America/Chicago',
        region: 'Central Time',
        designation: 'America/Chicago (CST/CDT)',
        preview: now.toLocaleString('en-US', { 
          timeZone: 'America/Chicago',
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
        })
      },
      {
        value: 'America/Denver',
        region: 'Mountain Time',
        designation: 'America/Denver (MST/MDT)',
        preview: now.toLocaleString('en-US', { 
          timeZone: 'America/Denver',
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
        })
      },
      {
        value: 'America/Los_Angeles',
        region: 'Pacific Time',
        designation: 'America/Los_Angeles (PST/PDT)',
        preview: now.toLocaleString('en-US', { 
          timeZone: 'America/Los_Angeles',
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
        })
      },
      {
        value: 'UTC',
        region: 'UTC',
        designation: 'Coordinated Universal Time',
        preview: now.toLocaleString('en-US', { 
          timeZone: 'UTC',
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true 
        })
      }
    ];
    
    return usTimezones;
  };
  
  const getOtherTimezones = () => {
    const now = new Date();
    const usTimezoneValues = ['LOCAL', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'UTC'];
    
    // Get all non-US timezones
    const otherTimezones = timezoneOptions
      .filter(option => !usTimezoneValues.includes(option.value))
      .map(option => {
        // Get UTC offset for this timezone
        const getUTCOffset = (timezone) => {
          try {
            // Calculate offset manually
            const utcDate = new Date(now.toLocaleString('en-US', {timeZone: 'UTC'}));
            const tzDate = new Date(now.toLocaleString('en-US', {timeZone: timezone}));
            const offset = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);
            
            const offsetHours = Math.floor(Math.abs(offset));
            const offsetMinutes = Math.round((Math.abs(offset) - offsetHours) * 60);
            const sign = offset >= 0 ? '+' : '-';
            
            return {
              offsetString: `UTC${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`,
              offsetValue: offset
            };
          } catch (error) {
            return {
              offsetString: 'UTC+00:00',
              offsetValue: 0
            };
          }
        };

        const offsetInfo = getUTCOffset(option.value);
        
        return {
          ...option,
          utcLabel: `${option.label} (${offsetInfo.offsetString})`,
          offsetValue: offsetInfo.offsetValue,
          offsetString: offsetInfo.offsetString
        };
      });

    // Show 18 international timezones (non-US) to complete the 24 total
    // US timezones (6) are already shown separately above
    // These match the backend's SUPPORTED_TIMEZONES list
    const preferredInternationalTimezones = [
      'Pacific/Midway',           // UTC-11 (Samoa)
      'Pacific/Honolulu',         // UTC-10 (Hawaii)
      'America/Anchorage',        // UTC-9 (Alaska)
      'America/Mexico_City',      // UTC-6 (Mexico City)
      'America/Bogota',           // UTC-5 (Bogota)
      'America/Caracas',          // UTC-4 (Caracas)
      'America/Santiago',         // UTC-3 (Santiago)
      'America/St_Johns',         // UTC-3:30 (Newfoundland)
      'Atlantic/Cape_Verde',      // UTC-1 (Cape Verde)
      'Europe/London',            // UTC+0 (London)
      'Europe/Paris',             // UTC+1 (Paris)
      'Europe/Athens',            // UTC+2 (Athens)
      'Europe/Moscow',            // UTC+3 (Moscow)
      'Asia/Dubai',               // UTC+4 (Dubai)
      'Asia/Karachi',             // UTC+5 (Karachi)
      'Asia/Dhaka',               // UTC+6 (Dhaka)
      'Asia/Bangkok',             // UTC+7 (Bangkok)
      'Asia/Shanghai',            // UTC+8 (Shanghai)
      'Asia/Tokyo',               // UTC+9 (Tokyo)
      'Australia/Sydney',         // UTC+10 (Sydney)
      'Pacific/Noumea',           // UTC+11 (New Caledonia)
      'Pacific/Auckland'          // UTC+12 (Auckland)
    ];

    // Filter to only include the preferred international timezones
    const filteredTimezones = otherTimezones.filter(tz => 
      preferredInternationalTimezones.includes(tz.value)
    );

    // Sort by offset value
    return filteredTimezones.sort((a, b) => a.offsetValue - b.offsetValue);
  };
  
  const isUSTimezone = (timezone) => {
    const usTimezoneValues = ['LOCAL', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'UTC'];
    return usTimezoneValues.includes(timezone);
  };
  
  const getCurrentTimezoneLabel = (timezone) => {
    const option = timezoneOptions.find(opt => opt.value === timezone);
    return option ? option.label : timezone;
  };
  
  const handleTimezoneChange = async (newTimezone) => {
    setSaving(true);
    setSavedMessage('');
    
    try {
      // Update the timezone configuration
      setTimezoneConfig(newTimezone);
      
      // Update local state
      setCurrentTimezone(newTimezone);
      setTimezoneInfo(getTimezoneInfo());
      
      // Show success message
      setSavedMessage('Timezone updated successfully!');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSavedMessage(''), 3000);
      
    } catch (error) {
      console.error('Failed to update timezone:', error);
      setSavedMessage('Failed to update timezone. Please try again.');
      
      // Auto-hide error message after 5 seconds
      setTimeout(() => setSavedMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };
  
  const handleAutoFillChange = async (settingType, newValue) => {
    setSaving(true);
    setSavedMessage('');
    
    try {
      // Handle different setting types
      if (settingType === 'master') {
        setAutoFillEnabled(newValue);
        // Don't automatically clear individual settings when master is disabled
        // Individual settings remain as they are for when master is re-enabled
      } else if (settingType === 'database') {
        setAutoFillFromDatabase(newValue);
      } else if (settingType === 'drafts') {
        setAutoFillFromDrafts(newValue);
      }
      
      // Update local state
      const updatedSettings = getAutoFillSettings();
      setAutoFillSettingsState(updatedSettings);
      
    } catch (error) {
      console.error('Failed to update auto-fill settings:', error);
      setSavedMessage('Failed to update auto-fill settings. Please try again.');
      
      // Auto-hide error message after 5 seconds
      setTimeout(() => setSavedMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
              <p className="text-gray-600">Manage your account preferences and configuration</p>
            </div>
          </div>
          
          {/* Context Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm">
                <p className="font-medium text-blue-900">Current Context</p>
                <p className="text-blue-700">
                  <span className="font-medium">Organization:</span> {currentOrganization?.name || 'Loading...'}
                  {currentSchool && (
                    <>
                      <span className="mx-2">•</span>
                      <span className="font-medium">School:</span> {currentSchool.name}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Settings Sections */}
        <div className="space-y-8">
          
          {/* Profile Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Profile Information
              </h2>
            </div>
            <div className="px-6 py-6">
              <div className="flex items-center gap-6">
                <img
                  src={user?.picture || "https://randomuser.me/api/portraits/men/14.jpg"}
                  alt="Profile"
                  className="w-20 h-20 rounded-full border-4 border-white shadow-lg"
                />
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">{user?.name || 'User'}</h3>
                  <p className="text-gray-600 mb-2">{user?.email || 'user@example.com'}</p>
                  <p className="text-sm text-gray-500">
                    Last login: {user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'Unknown'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Timezone Settings Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Timezone Configuration
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure how dates and times are displayed throughout the application
              </p>
            </div>
            
            <div className="px-6 py-6">
              <div className="space-y-6">
                
                {/* Current Timezone Info */}
                {timezoneInfo && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Current Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Setting:</span>
                        <span className="ml-2 text-gray-900">{timezoneInfo.config}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Resolved Timezone:</span>
                        <span className="ml-2 text-gray-900">{timezoneInfo.resolvedTimezone || 'Local Device'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Sample Date (Today):</span>
                        <span className="ml-2 text-gray-900">{timezoneInfo.sampleFormatted}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Readable Format:</span>
                        <span className="ml-2 text-gray-900">{timezoneInfo.sampleReadable}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Timezone Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Select Timezone
                  </label>
                  
                  {/* US Timezones Grid */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-600 mb-3">US Timezones</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {getUSTimezones().map((option) => {
                        const isSelected = currentTimezone === option.value;
                        
                        return (
                          <button
                            key={option.value}
                            onClick={() => handleTimezoneChange(option.value)}
                            disabled={saving}
                            className={`
                              relative p-4 rounded-lg border text-left transition-all
                              ${isSelected 
                                ? 'border-blue-500 bg-blue-50 text-blue-900' 
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }
                              ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium">{option.region}</div>
                                <div className="text-sm text-gray-600 mt-1">{option.designation}</div>
                                <div className="text-xs text-gray-500 mt-2">
                                  {option.preview}
                                </div>
                              </div>
                              {isSelected && (
                                <div className="text-blue-600">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Other Timezones Dropdown */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 mb-3">Other Timezones</h4>
                    <div className="relative">
                      <select
                        value={isUSTimezone(currentTimezone) ? '' : currentTimezone}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleTimezoneChange(e.target.value);
                          }
                        }}
                        disabled={saving}
                        className={`
                          w-full p-3 border border-gray-300 rounded-lg text-sm
                          ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                          focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                        `}
                      >
                        <option value="">
                          {isUSTimezone(currentTimezone) 
                            ? 'Select a different timezone...' 
                            : getCurrentTimezoneLabel(currentTimezone)
                          }
                        </option>
                        {getOtherTimezones().map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.utcLabel}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* Save Status */}
                {savedMessage && (
                  <div className={`
                    p-4 rounded-lg flex items-center gap-3
                    ${savedMessage.includes('successfully') 
                      ? 'bg-green-50 border border-green-200 text-green-800' 
                      : 'bg-red-50 border border-red-200 text-red-800'
                    }
                  `}>
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      {savedMessage.includes('successfully') ? (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      ) : (
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      )}
                    </svg>
                    <span className="font-medium">{savedMessage}</span>
                  </div>
                )}
                
                {/* Impact Notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.864-.833-2.634 0L3.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-sm">
                      <p className="font-medium text-amber-900">Timezone Change Impact</p>
                      <p className="text-amber-700 mt-1">
                        Changing the timezone will affect how dates are displayed across the entire application, including attendance reports, student tracking, and class schedules. 
                        The change takes effect immediately.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Auto-Fill Settings Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Auto-Fill Settings
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Control automatic data loading to optimize performance
              </p>
            </div>
            
            <div className="px-6 py-6">
              <div className="space-y-4">
                
                {/* Combined Auto-Fill Controls Box */}
                <div className="border border-blue-200 rounded-lg overflow-hidden">
                  {/* Master Auto-Fill Toggle */}
                  <div className="bg-blue-50 border-b border-blue-200 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-blue-900 flex items-center gap-2">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Master Auto-Fill Control
                        </div>
                        <div className="text-sm text-blue-700 mt-1">
                          Enable or disable all automatic data loading features. When disabled, improves performance but requires manual data entry.
                        </div>
                      </div>
                      <button
                        onClick={() => handleAutoFillChange('master', !autoFillSettings.enabled)}
                        disabled={saving}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                          ${autoFillSettings.enabled ? 'bg-blue-600' : 'bg-gray-200'}
                          ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            autoFillSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  
                  {/* Individual Controls */}
                  <div className={`p-4 transition-opacity ${!autoFillSettings.enabled ? 'opacity-50' : ''}`}>
                    <h4 className="font-medium text-gray-900 mb-4">Individual Controls</h4>
                  
                  <div className="space-y-4">
                    {/* Database Auto-Fill */}
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                          </svg>
                          Database Auto-Fill
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          Automatically load existing attendance records from the database when available.
                        </div>
                      </div>
                      <button
                        onClick={() => handleAutoFillChange('database', !autoFillSettings.databaseEnabled)}
                        disabled={saving || !autoFillSettings.enabled}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                          ${autoFillSettings.databaseEnabled && autoFillSettings.enabled ? 'bg-blue-600' : 'bg-gray-200'}
                          ${saving || !autoFillSettings.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            autoFillSettings.databaseEnabled && autoFillSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    
                    {/* Divider */}
                    <div className="border-t border-gray-100"></div>
                    
                    {/* Draft Auto-Fill */}
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Draft Auto-Fill
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          Automatically restore previously saved draft attendance data from browser storage.
                        </div>
                      </div>
                      <button
                        onClick={() => handleAutoFillChange('drafts', !autoFillSettings.draftsEnabled)}
                        disabled={saving || !autoFillSettings.enabled}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                          ${autoFillSettings.draftsEnabled && autoFillSettings.enabled ? 'bg-blue-600' : 'bg-gray-200'}
                          ${saving || !autoFillSettings.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            autoFillSettings.draftsEnabled && autoFillSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Notification Preferences Section - Commented out temporarily */}
          {/*
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                Notification Preferences
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure how you receive notifications and alerts
              </p>
            </div>
            <div className="px-6 py-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">Email Notifications</div>
                    <div className="text-sm text-gray-600">Receive email alerts for important events</div>
                  </div>
                  <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1" />
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">Attendance Reminders</div>
                    <div className="text-sm text-gray-600">Get reminded to take attendance for your classes</div>
                  </div>
                  <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-indigo-600 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6" />
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">Weekly Reports</div>
                    <div className="text-sm text-gray-600">Receive weekly attendance summary reports</div>
                  </div>
                  <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-indigo-600 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          */}
          
          {/* Security Section - Commented out temporarily */}
          {/*
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Security & Privacy
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Manage your account security settings
              </p>
            </div>
            <div className="px-6 py-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <div className="font-medium text-gray-900">Two-Factor Authentication</div>
                    <div className="text-sm text-gray-600">Add an extra layer of security to your account</div>
                  </div>
                  <button className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
                    Configure
                  </button>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div>
                    <div className="font-medium text-gray-900">Active Sessions</div>
                    <div className="text-sm text-gray-600">Manage devices signed into your account</div>
                  </div>
                  <button className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    View Sessions
                  </button>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div>
                    <div className="font-medium text-gray-900">Download Data</div>
                    <div className="text-sm text-gray-600">Export your account data and activity</div>
                  </div>
                  <button className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    Request Export
                  </button>
                </div>
              </div>
            </div>
          </div>
          */}
          
        </div>
        
        {/* Footer */}
        <div className="mt-12 text-center text-sm text-gray-500">
          <p>Settings are saved automatically and apply to your entire ArcAttend experience</p>
        </div>
      </div>
    </div>
    </div>
  );
}
